/**
 * Hatim ilerleme hesapları — mobil sonuç ekranı (results/hatim/[id].tsx) ile
 * aynı kurallar, istemci tarafında:
 * - Kuran / Cevşen (aralık tipi): yalnızca sayfa aralıkları sayılır.
 * - Diğer tipler: yalnızca assignedAmount / completedAmount sayılır.
 * - Hatimin toplam yüzdesi hatimin totalAmount'ına bölünür.
 * Veri kaynağı: mevcut `/api/hatim-instances/get-my-hatim-instance-groups`.
 */
import { isRangeType } from "./hatimType";
import { sumPages } from "./hatimEditing";

export function computeMemberProgress(assignment, type) {
  const a = assignment || {};
  const rangeBased = isRangeType(type);
  const assignedAmount = rangeBased
    ? sumPages(a.assignedPageRanges)
    : Math.max(0, Number(a.assignedAmount) || 0);
  const completedAmount = rangeBased
    ? sumPages(a.completedPageRanges)
    : Math.max(0, Number(a.completedAmount) || 0);
  const percent =
    assignedAmount > 0 ? Math.min(100, Math.round((completedAmount / assignedAmount) * 100)) : 0;
  const status = percent >= 100 ? "completed" : percent > 0 ? "in_progress" : "pending";
  return {
    assignedAmount,
    completedAmount,
    remaining: Math.max(0, assignedAmount - completedAmount),
    percent,
    status,
  };
}

export function summarizeProgress(members, totalAmount) {
  const list = Array.isArray(members) ? members : [];
  const totalAssigned = list.reduce((s, m) => s + (Number(m.assignedAmount) || 0), 0);
  const totalCompleted = list.reduce((s, m) => s + (Number(m.completedAmount) || 0), 0);
  const total = Math.max(0, Number(totalAmount) || 0);
  const denominator = total > 0 ? total : totalAssigned;
  return {
    memberCount: list.length,
    completedCount: list.filter((m) => m.status === "completed").length,
    totalAmount: total,
    totalAssigned,
    totalCompleted,
    unassigned: Math.max(0, total - totalAssigned),
    percent:
      denominator > 0 ? Math.min(100, Math.round((totalCompleted / denominator) * 100)) : 0,
  };
}

/** Sunucunun düz `users[]` satırını ekran satırına çevirir. */
export function toMember(row, type) {
  const r = row || {};
  return {
    userId: r.user ? String(r.user) : null,
    name: r.userName || r.name || "",
    surname: r.surname || "",
    email: r.email || "",
    assignedPageRanges: Array.isArray(r.assignedPageRanges) ? r.assignedPageRanges : [],
    completedPageRanges: Array.isArray(r.completedPageRanges) ? r.completedPageRanges : [],
    ...computeMemberProgress(r, type),
  };
}

/**
 * `get-my-hatim-instance-groups` yanıtından tek şablonun görünümünü kurar.
 * instanceId verilmezse aktif dönem, yoksa en son dönem seçilir.
 */
export function buildProgressView(groups, hatimTemplateId, instanceId) {
  const list = Array.isArray(groups) ? groups : [];
  const group = list.find((g) => String(g.template) === String(hatimTemplateId)) || null;
  const type = group?.templateType || "";
  const instances = (group?.instances || [])
    .slice()
    .sort((a, b) => Number(b.currentPeriod || 0) - Number(a.currentPeriod || 0));

  const periods = instances.map((inst) => {
    const members = (inst.users || []).map((u) => toMember(u, inst.type || type));
    return {
      id: String(inst.id || inst._id),
      currentPeriod: Number(inst.currentPeriod || 1),
      active: Boolean(inst.active) && !inst.isCompleted,
      isCompleted: Boolean(inst.isCompleted),
      startDate: inst.startDate,
      endDate: inst.endDate,
      percent: summarizeProgress(members, inst.totalAmount).percent,
    };
  });

  const requested = instanceId
    ? instances.find((i) => String(i.id || i._id) === String(instanceId))
    : null;
  const inst =
    requested ||
    instances.find((i) => Boolean(i.active) && !i.isCompleted) ||
    instances[0] ||
    null;

  if (!inst) {
    return { found: Boolean(group), isRangeType: isRangeType(type), periods, instance: null, members: [], summary: null };
  }

  const instType = inst.type || type;
  const members = (inst.users || []).map((u) => toMember(u, instType));
  return {
    found: true,
    isRangeType: isRangeType(instType),
    periods,
    instance: {
      id: String(inst.id || inst._id),
      currentPeriod: Number(inst.currentPeriod || 1),
      active: Boolean(inst.active) && !inst.isCompleted,
      isCompleted: Boolean(inst.isCompleted),
      startDate: inst.startDate,
      endDate: inst.endDate,
      totalAmount: inst.totalAmount,
    },
    members,
    summary: summarizeProgress(members, inst.totalAmount),
  };
}

export function assignedLabel(member) {
  const ranges = Array.isArray(member?.assignedPageRanges) ? member.assignedPageRanges : [];
  if (ranges.length > 0) {
    return ranges.map((r) => `${r.startPage}-${r.endPage}`).join(", ");
  }
  const amount = Number(member?.assignedAmount);
  if (Number.isFinite(amount) && amount > 0) return String(amount);
  return "—";
}

function fullName(m) {
  return `${m?.name || ""} ${m?.surname || ""}`.trim().toLowerCase();
}

/** Mobil ile aynı: tamamlayanlar üstte (yüzde azalan), eşitlikte ada göre (tr). Kopya döner. */
export function sortMembers(members) {
  const list = Array.isArray(members) ? members.slice() : [];
  list.sort((a, b) => {
    const pa = Number(a?.percent) || 0;
    const pb = Number(b?.percent) || 0;
    if (pa !== pb) return pb - pa;
    return fullName(a).localeCompare(fullName(b), "tr");
  });
  return list;
}

const STATUS_KEYS = {
  completed: "hatim_progress_status_completed",
  in_progress: "hatim_progress_status_in_progress",
  pending: "hatim_progress_status_pending",
};

export function statusKey(status) {
  return STATUS_KEYS[status] || STATUS_KEYS.pending;
}
